/**
 * Public real-time service facade for the Alveary frontend.
 *
 * Composes the shared Socket.IO transport (client / auth / reconnect) with
 * the per-domain live-update modules under ./domains. The public method
 * signatures are the stable contract consumed by hooks, tokenEventHandler,
 * and the websocketCompat manager — adding a live domain means adding a
 * domains/ module and a delegating method here, not editing the transport.
 */

import type { SessionTokenStatsResponse } from '@/types/tokens'
import type { DispatchScheduleChangedBroadcast } from '@/types/dispatch/schedules'
import type {
  IntegrationEventName,
  IntegrationEventPayload,
} from '@/types/integrations/foundation'
import type {
  ModelSpendEventName,
  ModelSpendRecordedPayload,
} from '@/types/modelSpend'
import type { DashboardMetricsResponse } from '@/types/activity'
import type { ProfileMembershipBroadcastPayload } from '@/types/bootstrapProfiles'
import type {
  CollectorRolloutEvent,
  CollectorStatusEvent,
} from '@/types/collectors'
import type {
  AnalyticsDomain,
  AnalyticsSnapshotOf,
} from '@/types/analytics/bundles'
import type {
  SummarizationEventMessage,
  SummarizationEventName,
} from '@/types/summarization'
import { IntakeLiveApi } from './domains/intake'
import { IdeasLiveApi } from './domains/ideas'
import { ProjectLiveApi } from './domains/projects'
import type { ProjectEventMessage } from '@/types/projectEvents'
import type { IntakeEventPayload } from '@/types/intake'
import type { IdeaEventPayload } from '@/types/idea'
import { WebSocketClient } from './client'
import { ReconnectPolicy } from './reconnect'
import { AnalyticsLiveApi } from './domains/analytics'
import { CollectorsLiveApi } from './domains/collectors'
import { DashboardLiveApi } from './domains/dashboard'
import { HomeLiveApi } from './domains/homeLive'
import type { HomeLiveTick } from '@/types/home'
import { DispatchScheduleLiveApi } from './domains/dispatchSchedules'
import { GlobalEventsLiveApi } from './domains/events'
import { IntegrationsLiveApi } from './domains/integrations'
import { ModelCapabilitiesLiveApi } from './domains/modelCapabilities'
import { ModelSpendLiveApi } from './domains/modelSpend'
import { ProfilesLiveApi } from './domains/profiles'
import {
  SmemLiveApi,
  type SmemAskEventName,
  type SmemClaimEventHandler,
  type SmemConsolidationEventDispatchPayload,
  type SmemConsolidationEventName,
  type SmemEpisodeEventDispatchPayload,
  type SmemEpisodeEventName,
  type SmemEventName,
  type SmemEventPayload,
  type SmemGovernanceEventName,
  type SmemGovernanceEventPayload,
} from './domains/smem'
import type { SmemAskSpendGuardPayload } from '@/types/smem'
import type {
  ModelCapabilityEventName,
  ModelCapabilityEventPayload,
} from '@/types/modelCapabilities'
import { SummarizationLiveApi } from './domains/summarization'
import { TokensLiveApi } from './domains/tokens'

export class SimpleWebSocketService {
  private readonly policy = new ReconnectPolicy()
  private readonly client = new WebSocketClient(this.policy, () =>
    this.replaySubscriptions()
  )
  private readonly intake = new IntakeLiveApi(this.client)
  private readonly ideas = new IdeasLiveApi(this.client)
  private readonly projects = new ProjectLiveApi(this.client)
  private readonly tokens = new TokensLiveApi(this.client)
  private readonly globalEvents = new GlobalEventsLiveApi(this.client)
  private readonly dashboard = new DashboardLiveApi(this.client)
  private readonly homeLive = new HomeLiveApi(this.client)
  private readonly analytics = new AnalyticsLiveApi(this.client)
  private readonly collectors = new CollectorsLiveApi(this.client)
  private readonly profiles = new ProfilesLiveApi(this.client)
  private readonly smem = new SmemLiveApi(this.client)
  private readonly summarization = new SummarizationLiveApi(this.client)
  private readonly modelCapabilities = new ModelCapabilitiesLiveApi(this.client)
  private readonly integrations = new IntegrationsLiveApi(this.client)
  private readonly modelSpend = new ModelSpendLiveApi(this.client)
  private readonly dispatchSchedules = new DispatchScheduleLiveApi(this.client)

  /** Connect to the WebSocket server (shared promise across callers). */
  connect(): Promise<void> {
    return this.client.connect()
  }

  /** Register before connecting; the transport retains outage history across mounts. */
  subscribeToReconnect(callback: () => void): () => void {
    return this.client.subscribeToReconnect(callback)
  }

  /** Read the current connection status and subsequent native transitions. */
  subscribeToConnectionStatus(callback: (connected: boolean) => void): () => void {
    return this.client.subscribeToConnectionStatus(callback)
  }

  /** Rejoin every domain's rooms after a fresh connection or reconnect. */
  private replaySubscriptions(): void {
    this.projects.replay()
    this.intake.replay()
    this.ideas.replay()
    this.tokens.replay()
    this.dashboard.replay()
    this.homeLive.replay()
    this.analytics.replay()
    this.collectors.replay()
    this.profiles.replay()
    this.smem.replay()
    this.summarization.replay()
    this.modelCapabilities.replay()
    this.integrations.replay()
    this.modelSpend.replay()
    this.dispatchSchedules.replay()
  }

  /** Subscribe to the intake register's authorized invalidation stream. */
  subscribeToIntakeEvents(
    callback: (payload: IntakeEventPayload) => void
  ): () => void {
    return this.intake.subscribe(callback)
  }

  /** Subscribe to the Ideas register's authorized invalidation stream. */
  subscribeToIdeaEvents(
    callback: (payload: IdeaEventPayload) => void
  ): () => void {
    return this.ideas.subscribe(callback)
  }

  /** Acquire a room before connecting so cleanup cancels pending joins too. */
  subscribeToProject(
    projectId: string,
    callback?: (message: ProjectEventMessage) => void
  ): () => void {
    return this.projects.subscribe(projectId, callback)
  }

  /** Re-evaluate active room interests after a grant change. */
  refreshProjectSubscriptions(): void {
    this.projects.replay()
    this.intake.replay()
    this.ideas.replay()
  }

  /** Subscribe to session-specific token updates. */
  subscribeToSession(
    sessionId: string,
    callback: (data: SessionTokenStatsResponse) => void
  ): () => void {
    return this.tokens.subscribeToSession(sessionId, callback)
  }

  /** Subscribe to global token analytics updates. */
  subscribeToGlobalTokenUpdates(callback: (data: unknown) => void): () => void {
    return this.tokens.subscribeToGlobalTokenUpdates(callback)
  }

  /** Subscribe to global activity events for the landing page. */
  subscribeToGlobalEvents(callback: (data: unknown) => void): () => void {
    return this.globalEvents.subscribeToGlobalEvents(callback)
  }

  /** Subscribe to dashboard metric snapshots for one activity window. */
  subscribeToDashboardMetrics(
    days: number,
    callback: (data: DashboardMetricsResponse) => void
  ): () => void {
    return this.dashboard.subscribeToDashboardMetrics(days, callback)
  }

  /** Acquire live-read interest before connection; replay joins it on reconnect. */
  subscribeToHomeLive(callback: (tick: HomeLiveTick) => void): () => void {
    return this.homeLive.subscribe(callback)
  }

  /** Subscribe to analytics snapshots for one (domain, window). */
  subscribeToAnalytics<D extends AnalyticsDomain>(
    domain: D,
    window: number,
    callback: (data: AnalyticsSnapshotOf<D>) => void
  ): () => void {
    return this.analytics.subscribeToAnalytics(domain, window, callback)
  }

  /** Subscribe to collector fleet health push events. */
  subscribeToCollectorStatus(
    callback: (event: CollectorStatusEvent) => void
  ): () => void {
    return this.collectors.subscribeToCollectorStatus(callback)
  }

  /** Subscribe to rollout-progress pushes from the collectors room. */
  subscribeToRolloutProgress(
    callback: (event: CollectorRolloutEvent) => void
  ): () => void {
    return this.collectors.subscribeToRolloutProgress(callback)
  }

  /** Subscribe to profile changes (create, repin or rollback); the callback gets the profile's name. */
  subscribeToProfileChange(cb: (name: string) => void): () => void {
    return this.profiles.subscribeToProfileChange(cb)
  }

  /** Subscribe to profile member list changes; the callback gets the frame naming the profile and artefact. */
  subscribeToProfileMembershipChange(
    cb: (frame: ProfileMembershipBroadcastPayload) => void
  ): () => void {
    return this.profiles.subscribeToProfileMembershipChange(cb)
  }

  /** Subscribe to every subject-registry broadcast; the callback gets (event, payload). */
  subscribeToSmemEvents(
    callback: (event: SmemEventName, payload: SmemEventPayload) => void
  ): () => void {
    return this.smem.subscribeToSmemEvents(callback)
  }

  /**
   * Subscribe to the episode-composition + composition-policy broadcasts
   * (R-11); the callback gets (event, payload).
   */
  subscribeToSmemEpisodeEvents(
    callback: (
      event: SmemEpisodeEventName,
      payload: SmemEpisodeEventDispatchPayload
    ) => void
  ): () => void {
    return this.smem.subscribeToSmemEpisodeEvents(callback)
  }

  /**
   * Subscribe to the claim registry's broadcasts; the callback gets (event,
   * payload) with the payload its event name carries.
   */
  subscribeToSmemClaimEvents(callback: SmemClaimEventHandler): () => void {
    return this.smem.subscribeToSmemClaimEvents(callback)
  }

  /** Subscribe to the governance broadcasts; the callback gets (event, payload). */
  subscribeToSmemGovernanceEvents(
    callback: (
      event: SmemGovernanceEventName,
      payload: SmemGovernanceEventPayload
    ) => void
  ): () => void {
    return this.smem.subscribeToSmemGovernanceEvents(callback)
  }

  /** Subscribe to the five consolidation broadcasts; the callback gets (event, payload). */
  subscribeToSmemConsolidationEvents(
    callback: (
      event: SmemConsolidationEventName,
      payload: SmemConsolidationEventDispatchPayload
    ) => void
  ): () => void {
    return this.smem.subscribeToSmemConsolidationEvents(callback)
  }

  /** Subscribe to the ask broadcasts; the callback gets (event, payload). */
  subscribeToSmemAskEvents(
    callback: (
      event: SmemAskEventName,
      payload: SmemAskSpendGuardPayload
    ) => void
  ): () => void {
    return this.smem.subscribeToSmemAskEvents(callback)
  }

  /**
   * Subscribe to every model-capability governance broadcast; the callback
   * gets (event, payload).
   */
  subscribeToModelCapabilityEvents(
    callback: (
      event: ModelCapabilityEventName,
      payload: ModelCapabilityEventPayload
    ) => void
  ): () => void {
    return this.modelCapabilities.subscribeToModelCapabilityEvents(callback)
  }

  /** Subscribe to every integration broadcast; the callback gets (event, payload). */
  subscribeToIntegrationEvents(
    callback: (
      event: IntegrationEventName,
      payload: IntegrationEventPayload
    ) => void
  ): () => void {
    return this.integrations.subscribeToIntegrationEvents(callback)
  }

  /** Subscribe to the call record's broadcast; the callback gets (event, payload). */
  subscribeToModelSpend(
    callback: (event: ModelSpendEventName, payload: ModelSpendRecordedPayload) => void
  ): () => void {
    return this.modelSpend.subscribeToModelSpend(callback)
  }

  /** Subscribe to every disp_schedule_changed frame; the callback gets the payload. */
  subscribeToDispatchScheduleEvents(
    callback: (payload: DispatchScheduleChangedBroadcast) => void
  ): () => void {
    return this.dispatchSchedules.subscribeToDispatchScheduleEvents(callback)
  }

  /** Subscribe to one summariser broadcast; the callback gets the envelope. */
  subscribeToSummarizationEvent(
    event: SummarizationEventName,
    callback: (message: SummarizationEventMessage) => void
  ): () => void {
    return this.summarization.subscribeToSummarizationEvent(event, callback)
  }

  /**
   * Returns true when the last connection attempt was refused with
   * authz_denied. The provider polls this after connect() rejects to
   * surface awaiting-grant state.
   */
  isAuthzDenied(): boolean {
    return this.policy.isAuthzDenied()
  }

  /**
   * Register a callback invoked once when an authz_denied refusal is
   * received. Returns a cleanup function to deregister the callback.
   */
  onAuthzDenied(cb: () => void): () => void {
    return this.policy.onAuthzDenied(cb)
  }

  /** Disconnect from the server and reset all transport and domain state. */
  disconnect(): void {
    this.client.disconnect()
    this.policy.reset()
    this.intake.clear()
    this.ideas.clear()
    this.projects.clear()
    this.tokens.clear()
    this.dashboard.clear()
    this.homeLive.clear()
    this.analytics.clear()
    this.collectors.clear()
    this.profiles.clear()
    this.smem.clear()
    this.summarization.clear()
    this.modelCapabilities.clear()
    this.integrations.clear()
    this.modelSpend.clear()
    this.dispatchSchedules.clear()
  }

  /** True if the WebSocket is connected and ready. */
  isConnected(): boolean {
    return this.client.isConnected()
  }

  /** Connection status and attempt counts for monitoring. */
  getConnectionStats() {
    return this.client.getConnectionStats()
  }

  /** Emit an event to the WebSocket server. */
  emit(event: string, data: unknown): void {
    this.client.emit(event, data)
  }

  /** Subscribe to a raw WebSocket event; returns a cleanup function. */
  on(event: string, callback: (data: unknown) => void): () => void {
    return this.client.on(event, callback)
  }
}
